import { withRoot } from "@nova/with-root"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import { omit, memoize } from "nodash"
import {
    ignoredProjectKeys,
    IgnoredProjectKeysTuple,
    SidePanelGroup,
    SidePanelLayerOrGroup,
    SidePanelLayerType,
} from "@nova/constants"
import { RootState } from "integrations/redux/rootReducer"
import { PDFJob, Project } from "@nova/protobuf"
import { setActiveImageId } from "features/map/layers/slice"
import { selectPDFJobs, selectPDFJobsMap } from "plugins/PDFv2/PDFSlice"
import { selectGroupsMap } from "features/entity-caching/groups/selectors"
import { getAncestors } from "./utils"

const uiStateName = "side-panel-ui-v1"
type uiStateNameType = typeof uiStateName

enum ViewState {
    Default = "default",
    Detail = "detail",
    ConfirmDelete = "confirm_delete",
    Edit = "edit",
    Draw = "draw",
    ReviewDrawing = "review",
    Update = "update",
}

export enum SidePanelMode {
    layers = "layers",
    pdf = "pdf",
    upload = "upload",
}

/**
 * data: Data Modal / FlightView / Plugin
 * layer: Actual layer content such as video/image set
 */
type ActiveSidePanelModalType = "data" | "layer"

/**
 * fmv: Full Motion Video
 * vcf: Video Colorfinder
 * icf: Image Colorfinder
 * pano: Panorama
 */
type LayerContentType = "fmv" | "vcf" | "icf" | "pano" | "live" | "pdf" | "hotspot" | "iconimage"

export type Modal = {
    id?: string
    type?: ActiveSidePanelModalType
    layerContentType?: LayerContentType
}

type UnwrappedModal = {
    id: string
    type: ActiveSidePanelModalType
    layerContentType?: LayerContentType
}

type UIState = {
    drawPerimeterViewState?: ViewState
    activeSidePanelModal?: UnwrappedModal
    canDownload: boolean
    layerContentType: LayerContentType
    layerLoading: boolean
    localSortedIds: string[]
    modalIsOverlappingMapToolbox: boolean
    modalIsFullScreen: boolean
}

const initialState: UIState = {
    drawPerimeterViewState: undefined,
    activeSidePanelModal: undefined,
    canDownload: false,
    layerContentType: "fmv",
    layerLoading: false,
    localSortedIds: [],
    modalIsOverlappingMapToolbox: false,
    modalIsFullScreen: false,
}

const {
    reducer,
    actions: {
        setDrawPerimeterViewState,
        setActiveSidePanelModal,
        setActiveSidePanelModalIsLoading,
        setCanDownload,
        setLocalSortedIds,
        setModalIsOverlappingMapToolbox,
        setModalIsFullScreen,
    },
} = createSlice({
    name: uiStateName,
    initialState,
    reducers: {
        setDrawPerimeterViewState: (state, action: { payload: ViewState | undefined }) => {
            state.drawPerimeterViewState = action.payload
        },

        /**
         * payload: Flight ID or plugin ID of "open" modal or active layer like FMV/VCF/ICF/Pano media.
         */
        setActiveSidePanelModal: (state, action: { payload: Modal | undefined }) => {
            const modalPropsAreUndefined = !action.payload?.id || !action.payload.type

            state.activeSidePanelModal = modalPropsAreUndefined
                ? undefined
                : (action.payload as UnwrappedModal)

            state.layerLoading = !!action.payload && action.payload.type === "layer"
        },

        setActiveSidePanelModalIsLoading: (
            state,
            action: { payload: { id: string; loading: boolean } }
        ) => {
            if (action.payload.id !== state.activeSidePanelModal?.id) return
            state.layerLoading = action.payload.loading
        },

        setCanDownload: (state, action: { payload: boolean }) => {
            state.canDownload = action.payload
        },

        setLocalSortedIds: (state, action: { payload: string[] }) => {
            state.localSortedIds = action.payload
        },

        setModalIsOverlappingMapToolbox: (state, action: { payload: boolean }) => {
            state.modalIsOverlappingMapToolbox = action.payload
        },

        setModalIsFullScreen: (state, action: { payload: boolean }) => {
            state.modalIsFullScreen = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(setActiveImageId, (state, action) => {
            if (action.payload === undefined) return
            state.activeSidePanelModal = undefined
        })
    },
})

// Export selectors
export const {
    selectDrawPerimeterViewState,
    selectActiveSidePanelModal,
    selectActiveSidePanelModalIsLoading,
    selectCanDownload,
    selectLocalSortedIds,
    selectModalIsOverlappingMapToolbox,
    selectModalIsFullScreen,
} = withRoot((r: Record<uiStateNameType, UIState>) => r[uiStateName], {
    selectDrawPerimeterViewState: () => (s) => s.drawPerimeterViewState,
    selectActiveSidePanelModal: () => (s) => s.activeSidePanelModal,
    selectActiveSidePanelModalIsLoading: () => (s) => s.layerLoading,
    selectCanDownload: () => (s) => s.canDownload,
    selectLocalSortedIds: () => (s) => s.localSortedIds,
    selectModalIsOverlappingMapToolbox: () => (s) => s.modalIsOverlappingMapToolbox,
    selectModalIsFullScreen: () => (s) => s.modalIsFullScreen,
})

type EntityType = keyof Omit<Project, IgnoredProjectKeysTuple>
const sidePanelLayerTypeByProtobufEntityType: Record<EntityType, SidePanelLayerType> = {
    icons: SidePanelLayerType.Icon,
    lines: SidePanelLayerType.Line,
    rulers: SidePanelLayerType.Ruler,
    texts: SidePanelLayerType.Text,
    perimeters: SidePanelLayerType.FirePerimeter,
    PDFRasters: SidePanelLayerType.PDFRaster,
    infraredFlights: SidePanelLayerType.HotspotFlight,
    orthoFlights: SidePanelLayerType.OrthomosaicFlight,
    videoFlights: SidePanelLayerType.VideoFlight,
    videoColorFinderFlights: SidePanelLayerType.VideoColorFinder,
    imageColorFinderFlights: SidePanelLayerType.ImageColorFinder,
    panoramaFlights: SidePanelLayerType.Panorama,
    groups: SidePanelLayerType.Group,
    liveVideoFlights: SidePanelLayerType.LiveVideoFlight,
    mergedFlights: SidePanelLayerType.MergedFlight,
    mapComments: SidePanelLayerType.Comment,
    satelliteFlights: SidePanelLayerType.SatelliteFlight,
    userLocationShares: SidePanelLayerType.UserLocationShare,
}

export const selectAllEntities = createSelector(
    [(state: RootState) => state.project.data],
    (project) => {
        // Select all project data from Protobuf except for PDFs.
        const data = omit(project, ignoredProjectKeys) as Omit<Project, IgnoredProjectKeysTuple>

        const entities = Object.entries(data).flatMap(([entityType, entities]) => {
            //give mergedFlights the context of what infrared flights they contain
            if (entityType === "mergedFlights") {
                const infraredFlights = data.infraredFlights
                return entities.flatMap((mergedFlight) => {
                    const flightsInMerge = infraredFlights.filter(
                        (flight) => flight.mergeId === mergedFlight.id
                    )
                    const flightIdsInMerge = flightsInMerge.map((flight) => flight.id)
                    return {
                        ...mergedFlight,
                        hotspotFlightIds: flightIdsInMerge,
                        hotspotFlights: flightsInMerge,
                        type: sidePanelLayerTypeByProtobufEntityType[entityType as EntityType],
                    } as SidePanelLayerOrGroup
                })
            } // Apply a SidePanelLayerType to each entity, then flatMap.
            else
                return entities.flatMap(
                    (entity) =>
                        ({
                            ...entity,
                            type: sidePanelLayerTypeByProtobufEntityType[entityType as EntityType],
                        }) as SidePanelLayerOrGroup
                )
        })

        return entities
    }
)

export const selectAllEntitiesMap = createSelector([selectAllEntities], (allEntities) => {
    const entityById = allEntities.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, SidePanelLayerOrGroup>
    )

    return entityById
})

export const selectAncestorEntitiesByEntityId = memoize((id: string) =>
    createSelector([selectAllEntitiesMap], (entityMap) => {
        const ancestors = getAncestors(id, entityMap)
        return ancestors
    })
)

export const selectEntitiesFromIds = memoize(
    (ids: string[]) =>
        createSelector(
            [selectAllEntitiesMap, selectPdfEntitiesMap],
            (
                entityMap: Record<string, SidePanelLayerOrGroup>,
                pdfMap: Record<string, PDFJobWithChildren | SidePanelLayerOrGroup>
            ) => {
                return ids.reduce(
                    (acc, cur) => {
                        const entity = entityMap[cur] || pdfMap[cur]
                        acc.push(entity)
                        return acc
                    },
                    [] as (SidePanelLayerOrGroup | PDFJobWithChildren)[]
                )
            }
        ),
    (arr) => arr.join("|")
)

export const assetLayerTypes = [
    SidePanelLayerType.OrthomosaicFlight,
    SidePanelLayerType.HotspotFlight,
    SidePanelLayerType.FirePerimeter,
    SidePanelLayerType.ImageColorFinder,
    SidePanelLayerType.VideoColorFinder,
    SidePanelLayerType.LiveVideoFlight,
    SidePanelLayerType.VideoFlight,
    SidePanelLayerType.Panorama,
]

export const selectEntityIdsByCreatorId = createSelector([selectAllEntities], (allEntities) => {
    const entityByCreator = allEntities.reduce(
        (acc, cur) => {
            if (!cur.createdBy?.userId) return acc
            if (cur.createdBy.userId in acc) {
                acc[cur.createdBy.userId].push(cur.id)
            } else {
                acc[cur.createdBy.userId] = [cur.id]
            }
            return acc
        },
        {} as Record<string, string[]>
    )
    return entityByCreator
})

export const selectAssetEntityIdsByCreatorId = createSelector(
    [selectAllEntities],
    (allEntities) => {
        const entityByCreator = allEntities.reduce(
            (acc, cur) => {
                if (!cur.type || !assetLayerTypes.includes(cur.type)) return acc
                if (!cur.createdBy?.userId) return acc
                if (cur.createdBy.userId in acc) {
                    acc[cur.createdBy.userId].push(cur.id)
                } else {
                    acc[cur.createdBy.userId] = [cur.id]
                }
                return acc
            },
            {} as Record<string, string[]>
        )
        return entityByCreator
    }
)

type PDFJobWithChildren = PDFJob & {
    children: (PDFJobWithChildren | GroupWithType)[]
    type: SidePanelLayerType.PDF
}
type GroupWithType = SidePanelGroup & { type: SidePanelLayerType.Group }
export const selectPdfEntitiesMap = createSelector(
    [selectPDFJobsMap, selectGroupsMap],
    (pdfMap, groupsMap) => {
        return {
            ...Object.fromEntries(
                Object.entries(pdfMap).map(([key, value]) => [
                    key,
                    {
                        ...value,
                        children: [],
                        type: SidePanelLayerType.PDF,
                    } as PDFJobWithChildren,
                ])
            ),
            ...Object.fromEntries(
                Object.entries(groupsMap).map(([key, value]) => [
                    key,
                    {
                        ...value,
                        children: [],
                        type: SidePanelLayerType.Group,
                    } as GroupWithType,
                ])
            ),
        }
    }
)

export const selectPdfIdsByCreatorId = createSelector([selectPDFJobs], (pdfJobs) => {
    const pdfByCreator = pdfJobs.reduce(
        (acc, cur) => {
            if (!cur.createdBy?.userId) return acc
            if (cur.createdBy.userId in acc) {
                acc[cur.createdBy.userId].push(cur.id)
            } else {
                acc[cur.createdBy.userId] = [cur.id]
            }
            return acc
        },
        {} as Record<string, string[]>
    )
    return pdfByCreator
})

export const selectEntityById = memoize((id: string) =>
    createSelector(
        [selectAllEntitiesMap, selectPdfEntitiesMap],
        (entitiesMap, pdfEntitiesMap) => entitiesMap[id] || pdfEntitiesMap[id]
    )
)

export const selectChildrenEntityIds = memoize((id: string) =>
    createSelector([selectAllEntities, selectPDFJobs], (entities, pdfs) => [
        ...entities.filter((entity) => entity.parentId === id).map((entity) => entity.id),
        ...pdfs.filter((pdf) => pdf.parentId === id).map((pdf) => pdf.id),
    ])
)

// Export actions
export {
    setDrawPerimeterViewState,
    setActiveSidePanelModal,
    setActiveSidePanelModalIsLoading,
    setCanDownload,
    setLocalSortedIds,
    setModalIsOverlappingMapToolbox,
    setModalIsFullScreen,
}

export const uiReducer = { [uiStateName]: reducer }
